import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Sparkles, DollarSign, ArrowLeft, PawPrint } from "lucide-react";

const TIP_PRESETS = [0, 15, 20, 25];

export default function Pay() {
  const { appointmentId } = useParams();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tipPct, setTipPct] = useState(20);
  const [customTip, setCustomTip] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/appointments/${appointmentId}/payment-summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => toast.error("Couldn't load appointment"));
  }, [appointmentId]);

  if (!summary) {
    return <div className="min-h-screen grid place-content-center bg-[#FAF7F2] text-[#6B625B]">Loading…</div>;
  }
  if (summary.paid) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] grid place-content-center p-6">
        <div className="max-w-md bg-white rounded-3xl border border-[#E5DCCE] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-content-center mx-auto text-emerald-600">
            <Sparkles className="w-6 h-6"/>
          </div>
          <h1 className="font-display text-2xl font-bold mt-4">Already paid</h1>
          <p className="text-sm text-[#6B625B] mt-2">You paid ${summary.paid_amount?.toFixed(2)} — thank you!</p>
          <Button onClick={() => nav("/client")} className="mt-6 rounded-full bg-[#C85A17] hover:bg-[#A9480F]" data-testid="pay-back">
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  const service = Number(summary.price || 0);
  const tipAmount = customTip !== ""
    ? Math.max(0, Number(customTip) || 0)
    : +(service * (tipPct / 100)).toFixed(2);
  const total = +(service + tipAmount).toFixed(2);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        appointment_id: appointmentId,
        tip_amount: tipAmount,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] p-6">
      <div className="max-w-md mx-auto">
        <button onClick={() => nav(-1)} className="text-sm text-[#6B625B] flex items-center gap-1 mb-4"
          data-testid="pay-back-nav">
          <ArrowLeft className="w-4 h-4"/> Back
        </button>

        <div className="bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm space-y-5">
          <div>
            <div className="label-accent">Pay & tip</div>
            <h1 className="font-display text-3xl font-bold mt-1">Settle up</h1>
          </div>

          <div className="rounded-2xl bg-[#FAF7F2] border border-[#E5DCCE] p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#F5E6CF] grid place-content-center text-[#A9480F]">
              <PawPrint className="w-5 h-5"/>
            </div>
            <div>
              <div className="font-semibold">{summary.service_name}</div>
              <div className="text-xs text-[#6B625B]">for {summary.pet_name || "your pup"}</div>
            </div>
            <div className="ml-auto font-display text-xl font-bold">${service.toFixed(2)}</div>
          </div>

          <div>
            <Label>Add a tip for your groomer</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {TIP_PRESETS.map(p => (
                <button key={p}
                  onClick={() => { setTipPct(p); setCustomTip(""); }}
                  className={`rounded-2xl border-2 py-3 text-sm font-semibold transition-colors ${
                    customTip === "" && tipPct === p
                      ? "border-[#C85A17] bg-[#F5E6CF] text-[#A9480F]"
                      : "border-[#E5DCCE] bg-white text-[#6B625B] hover:border-[#C85A17]/40"
                  }`}
                  data-testid={`tip-${p}`}>
                  {p === 0 ? "None" : `${p}%`}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <Label className="text-xs text-[#6B625B]">Or custom amount ($)</Label>
              <Input type="number" min="0" step="0.5" value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                placeholder="e.g. 10"
                data-testid="tip-custom"/>
            </div>
          </div>

          <div className="border-t border-[#E5DCCE] pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[#6B625B]">Service</span><span>${service.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#6B625B]">Tip</span><span data-testid="tip-value">${tipAmount.toFixed(2)}</span></div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#E5DCCE] font-display">
              <span>Total</span><span data-testid="pay-total">${total.toFixed(2)}</span>
            </div>
          </div>

          <Button onClick={pay} disabled={busy}
            className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12"
            data-testid="btn-pay">
            {busy ? <Sparkles className="w-4 h-4 mr-2 animate-pulse"/> : <CreditCard className="w-4 h-4 mr-2"/>}
            {busy ? "Redirecting…" : `Pay $${total.toFixed(2)}`}
          </Button>

          <p className="text-[10px] text-center text-[#6B625B]">
            Secured by Stripe · Card, Apple Pay, Google Pay & Link
          </p>
        </div>
      </div>
    </div>
  );
}
